// --- Dependencies -------------------------------------------------------------------------------
import type { ChartOptions, ScriptableContext } from "chart.js";
// --- Types --------------------------------------------------------------------------------------

export type TyChartKind = "area" | "bar" | "stackedBar" | "hbar" | "doughnut";

/**
 * Slots, not colours. A canvas cannot resolve `var(--chart-1)` — it needs a literal — so call sites name
 * the slot they want and the value is read off the stylesheet here. That keeps `styles.css` the single
 * source of truth for the palette while still letting a card say what a series *means*.
 *
 * `good` / `warn` / `bad` are the reserved status tones and are never handed out as a categorical hue.
 */
export type TyChartColor = "brand" | "blue" | "green" | "amber" | "violet" | "pink" | "good" | "warn" | "bad" | "muted" | "faint";

export type TyChartTokens = {
    ink: string;
    muted: string;
    grid: string;
    surface: string;
    /** The categorical ramp, in the fixed order it must always be assigned in. */
    series: string[];
    byName: Record<TyChartColor, string>;
};

// --- Constants ----------------------------------------------------------------------------------

/** Every mark in the app is drawn in Alexandria, so the canvas has to be told — it inherits nothing. */
const FONT = "Alexandria, sans-serif";

/** Mirrors `--font-xs`. The canvas cannot read a CSS token, so the one number lives here. */
const FONT_SIZE = 11;

/** 4px on the data end only, so a bar still grows out of a flat baseline. */
const BAR_RADIUS = { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 };

const HBAR_RADIUS = { topLeft: 0, topRight: 4, bottomLeft: 0, bottomRight: 4 };

// --- Logic --------------------------------------------------------------------------------------

const SERIES_FALLBACK = ["#dd0d17", "#2a78d6", "#178f63", "#c98500", "#7a5ac8", "#d55181"];

export function readChartTokens(): TyChartTokens {
    const styles = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;

    const series = SERIES_FALLBACK.map((fallback, index) => read(`--chart-${index + 1}`, fallback));

    return {
        ink: read("--color-dark", "#181817"),
        muted: read("--color-gray-03", "#6b6b6b"),
        grid: read("--color-light", "#f5f5f5"),
        surface: read("--color-white", "#ffffff"),
        series,
        byName: {
            brand: series[0],
            blue: series[1],
            green: series[2],
            amber: series[3],
            violet: series[4],
            pink: series[5],
            good: read("--color-success", "#3fac89"),
            warn: read("--color-warning", "#d99e29"),
            bad: read("--color-error", "#cf4c4c"),
            muted: read("--color-gray-02", "#a4a4a4"),
            faint: read("--color-gray-01", "#d1d5db"),
        },
    };
}

/** A named slot, or the next categorical hue in fixed order when a series did not ask for one. */
export function resolveColor(name: TyChartColor | undefined, tokens: TyChartTokens, index: number): string {
    if (name) return tokens.byName[name];
    return tokens.series[index % tokens.series.length];
}

/** `#rrggbb` plus an alpha byte. Chart fills need transparency that the tokens themselves must not carry. */
export function withAlpha(hex: string, alpha: number): string {
    const byte = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
        .toString(16)
        .padStart(2, "0");
    return `${hex}${byte}`;
}

/**
 * A vertical fade under an area line. Built as a scriptable option rather than a fixed colour because
 * the gradient needs the canvas height, which does not exist until the chart has been laid out — on the
 * first render `chartArea` is still undefined, hence the guard.
 */
function areaFill(color: string) {
    return (context: ScriptableContext<"line">) => {
        const { ctx, chartArea } = context.chart;
        if (!chartArea) return withAlpha(color, 0.12);
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, withAlpha(color, 0.28));
        gradient.addColorStop(1, withAlpha(color, 0));
        return gradient;
    };
}

/**
 * One options object for every chart on the dashboard, so grid weight, tick size and tooltip styling
 * cannot drift between two cards sitting side by side. Anything a single chart needs differently is a
 * parameter here rather than an override at the call site.
 */
export function buildOptions(
    kind: TyChartKind,
    tokens: TyChartTokens,
    isRtl: boolean,
    formatValue: (value: number) => string,
    hasLegend: boolean
): ChartOptions<any> {
    const tooltip = {
        backgroundColor: tokens.ink,
        titleColor: tokens.surface,
        bodyColor: tokens.surface,
        titleFont: { family: FONT, size: FONT_SIZE, weight: 600 as const },
        bodyFont: { family: FONT, size: FONT_SIZE },
        padding: 10,
        cornerRadius: 8,
        displayColors: kind !== "doughnut",
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 4,
        usePointStyle: true,
        rtl: isRtl,
        textDirection: isRtl ? ("rtl" as const) : ("ltr" as const),
        callbacks: {
            label: (item: any) => {
                const label = item.dataset?.label ?? item.label ?? "";
                const value = formatValue(Number(item.parsed?.y ?? item.parsed ?? 0));
                return label ? ` ${label}: ${value}` : ` ${value}`;
            },
        },
    };

    const legend = {
        display: hasLegend,
        position: "bottom" as const,
        align: "start" as const,
        rtl: isRtl,
        textDirection: isRtl ? ("rtl" as const) : ("ltr" as const),
        labels: {
            /** Text wears ink, never the series colour — the swatch beside it carries the identity. */
            color: tokens.muted,
            font: { family: FONT, size: FONT_SIZE },
            usePointStyle: true,
            pointStyle: "circle" as const,
            boxWidth: 8,
            boxHeight: 8,
            padding: 14,
        },
    };

    const ticks = { color: tokens.muted, font: { family: FONT, size: FONT_SIZE }, padding: 4 };

    const base: ChartOptions<any> = {
        responsive: true,
        maintainAspectRatio: false,
        /** Recessive by design: the marks carry the story, the frame only has to stay out of the way. */
        interaction: { mode: "index" as const, intersect: false },
        animation: { duration: 320 },
        plugins: { legend, tooltip },
    };

    if (kind === "doughnut") {
        return {
            ...base,
            cutout: "66%",
            /** A 2px ring in the card's own colour, so touching slices never bleed into one another. */
            borderColor: tokens.surface,
            borderWidth: 2,
            interaction: { mode: "nearest" as const, intersect: true },
            plugins: { ...base.plugins, legend: { ...legend, display: true } },
        };
    }

    const valueAxis = {
        beginAtZero: true,
        border: { display: false },
        grid: { color: tokens.grid, drawTicks: false },
        /**
         * `precision: 0` because every measure on this dashboard is a whole thing — a store, a request,
         * a pound. Left to itself chart.js divides a max of 1 into 0 / 0.5 / 1, and half a store is not
         * a quantity that exists.
         */
        ticks: { ...ticks, maxTicksLimit: 5, precision: 0, callback: (value: number | string) => formatValue(Number(value)) },
    };

    const categoryAxis = {
        border: { display: false },
        grid: { display: false },
        ticks: { ...ticks, maxRotation: 0, autoSkipPadding: 12 },
    };

    if (kind === "hbar") {
        return {
            ...base,
            indexAxis: "y" as const,
            interaction: { mode: "nearest" as const, intersect: true },
            scales: { x: valueAxis, y: categoryAxis },
        };
    }

    return {
        ...base,
        scales: { x: categoryAxis, y: valueAxis },
        ...(kind === "stackedBar" ? { scales: { x: { ...categoryAxis, stacked: true }, y: { ...valueAxis, stacked: true } } } : {}),
    };
}

/**
 * Turns one logical series into a chart.js dataset. Kept here beside the options so a mark's geometry —
 * line weight, point size, bar thickness, the gap between stacked segments — is decided in one file.
 */
export function buildDataset(kind: TyChartKind, label: string, data: number[], color: string, tokens: TyChartTokens, colors?: string[]) {
    if (kind === "doughnut") return { label, data, backgroundColor: colors ?? [color], borderColor: tokens.surface, borderWidth: 2, hoverOffset: 6 };

    if (kind === "area") {
        return {
            label,
            data,
            borderColor: color,
            backgroundColor: areaFill(color),
            borderWidth: 2,
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            /** Invisible until hovered, then big enough to aim at — 8px is the smallest honest target. */
            pointHoverRadius: 5,
            pointHoverBorderWidth: 2,
            pointHoverBorderColor: tokens.surface,
            pointHoverBackgroundColor: color,
            pointBackgroundColor: color,
        };
    }

    return {
        label,
        data,
        backgroundColor: colors ?? color,
        borderRadius: kind === "hbar" ? HBAR_RADIUS : BAR_RADIUS,
        borderSkipped: false,
        /** The 2px surface gap that keeps stacked segments from reading as one solid block. */
        borderColor: tokens.surface,
        borderWidth: kind === "stackedBar" ? { top: 2, right: 0, bottom: 0, left: 0 } : 0,
        maxBarThickness: kind === "hbar" ? 14 : 28,
    };
}
